/* eslint-disable react/jsx-props-no-spreading */
import { Alert, Box, Button, Grid, TextField } from '@mui/material';
import PropTypes from 'prop-types';
import { memo, useCallback, useMemo } from 'react';

import InputAutocompleteQuery from 'components/InputAutocompleteQuery';
import useForm from 'hooks/useForm';
import useDealsByModelQuery from 'modules/Deal/hooks/useDealsByModelQuery';
import { confirmMissingCompanyWithSwal } from 'utils/confirmMissingCompany';
import { COLOR_ERROR, COLOR_PRIMARY } from 'utils/constants/colors';

import {
  FIELD_COMPANY,
  FIELD_CONTACT,
  FIELD_DESCRIPTION,
  FIELD_ID,
  FIELD_PRIORITY,
  FIELD_SITE,
  FIELD_STATUS,
  FIELD_SYSTEM,
  FIELD_TITLE,
} from '../constants/fields';
import {
  PRIORITY_HIGH,
  PRIORITY_LOW,
  PRIORITY_MEDIUM,
  PRIORITY_OPTIONS,
} from '../constants/priorities';
import {
  STATUS_CLOSED,
  STATUS_ON_HOLD,
  STATUS_OPEN,
  STATUS_OPTIONS,
  STATUS_RESOLVED,
} from '../constants/statuses';

export const INITIAL_VALUES = {
  [FIELD_CONTACT]: null,
  [FIELD_SITE]: null,
  [FIELD_COMPANY]: null,
  [FIELD_SYSTEM]: null,
  [FIELD_TITLE]: '',
  [FIELD_DESCRIPTION]: '',
  [FIELD_STATUS]: STATUS_OPEN,
  [FIELD_PRIORITY]: PRIORITY_LOW,
};

const TaskForm = ({
  submitting = false,
  cancelLabel = 'Cancel',
  submitLabel = 'Submit',
  submitLoadingLabel = 'Submitting…',
  errorMessage = null,
  values: initialValues = INITIAL_VALUES,
  errors: initialErrors = {},
  contactFieldOptions = [],
  siteFieldOptions = [],
  companyFieldOptions = [],
  systemFieldOptions = [],
  statusFieldOptions = STATUS_OPTIONS,
  priorityFieldOptions = PRIORITY_OPTIONS,
  onChange,
  onCancel,
  onSubmit,
}) => {
  // Hooks
  const handleSubmit = useCallback(
    async (values, formikHelpers) => {
      let forceWithoutCompany = false;

      if (!values?.[FIELD_ID]) {
        const siteId = values?.[FIELD_SITE];
        const companyId = values?.[FIELD_COMPANY];

        if (siteId && !companyId) {
          const { confirmed, forceWithoutCompany: shouldForceWithoutCompany } =
            await confirmMissingCompanyWithSwal({
              siteId,
              hasCompany: Boolean(companyId),
              entityLabel: 'task',
            });

          if (!confirmed) {
            formikHelpers?.setSubmitting?.(false);
            return;
          }

          forceWithoutCompany = shouldForceWithoutCompany;
        }
      }

      const nextValues = forceWithoutCompany
        ? { ...values, force_without_company: true }
        : values;

      await onSubmit(nextValues, formikHelpers);
    },
    [onSubmit],
  );

  const form = useForm({
    initialValues,
    initialErrors,
    onChange,
    onSubmit: handleSubmit,
  });

  const selectedContactId = form.values?.[FIELD_CONTACT];

  useDealsByModelQuery({
    model: 'contact',
    modelId: selectedContactId,
    cacheTime: 0,
    staleTime: 0,
    onSuccess: (_deals) => {
      const deal = _deals.at(0);

      if (deal) {
        form.setValues((currentValues) => {
          if (
            currentValues[FIELD_CONTACT] !== selectedContactId ||
            currentValues[FIELD_SITE] ||
            currentValues[FIELD_COMPANY] ||
            currentValues[FIELD_SYSTEM]
          ) {
            return currentValues;
          }

          return {
            ...currentValues,
            [FIELD_SITE]: deal.site_id,
            [FIELD_COMPANY]: deal.company_id,
            [FIELD_SYSTEM]: deal.systems?.at(0)?.id ?? null,
          };
        });
      }
    },
  });

  // Handlers

  const handleCancelClick = useCallback(() => onCancel(), [onCancel]);

  const handleContactChange = useCallback(
    (contactId) => {
      form.setValues((currentValues) => {
        if (currentValues[FIELD_CONTACT] === contactId) {
          return currentValues;
        }

        return {
          ...currentValues,
          [FIELD_CONTACT]: contactId,
        };
      });
    },
    [form],
  );

  const handleSiteChange = useCallback(
    (siteId) => {
      form.setValues((currentValues) => {
        if (currentValues[FIELD_SITE] === siteId) {
          return currentValues;
        }

        return {
          ...currentValues,
          [FIELD_SITE]: siteId,
        };
      });
    },
    [form],
  );

  const handleCompanyChange = useCallback(
    (companyId) => {
      form.setValues((currentValues) => {
        if (currentValues[FIELD_COMPANY] === companyId) {
          return currentValues;
        }

        return {
          ...currentValues,
          [FIELD_COMPANY]: companyId,
        };
      });
    },
    [form],
  );

  // Computable

  const siteFieldOptionsParams = useMemo(
    () => ({
      contact_id: form.values[FIELD_CONTACT],
    }),
    [form.values],
  );

  const companyFieldOptionsParams = useMemo(
    () => ({
      contact_id: form.values[FIELD_CONTACT],
    }),
    [form.values],
  );

  const systemFieldOptionsParams = useMemo(
    () => ({
      contact_id: form.values[FIELD_CONTACT],
      company_id: form.values[FIELD_COMPANY],
      site_id: form.values[FIELD_SITE],
    }),
    [form.values],
  );

  const submitDisabled = useMemo(() => {
    if (submitting) {
      return true;
    }

    return (
      !form.values[FIELD_CONTACT] ||
      !form.values[FIELD_SITE] ||
      !form.values[FIELD_TITLE] ||
      !form.values[FIELD_DESCRIPTION] ||
      !form.values[FIELD_PRIORITY] ||
      !form.values[FIELD_STATUS]
    );
  }, [form.values, submitting]);

  // Render

  return (
    <Box component="form" onSubmit={form.handleSubmit}>
      <Grid container={true} spacing={3}>
        {errorMessage && (
          <Grid item={true} md={12} xs={12}>
            <Alert severity={COLOR_ERROR}>{errorMessage}</Alert>
          </Grid>
        )}
        <Grid item={true} md={6} xs={12}>
          <InputAutocompleteQuery
            disabled={submitting}
            queryKeyPrefix="task_contact"
            label="Contact"
            required={true}
            placeholder="Search Contacts…"
            options={contactFieldOptions}
            value={form.values[FIELD_CONTACT] ?? null}
            onChange={handleContactChange}
            {...form.getMuiFieldErrorProps(FIELD_CONTACT)}
            data-testid="contact_field"
          />
        </Grid>
        <Grid item={true} md={6} xs={12}>
          <InputAutocompleteQuery
            disabled={submitting}
            queryKeyPrefix="task_site"
            label="Site"
            required={true}
            placeholder="Search Sites…"
            options={siteFieldOptions}
            optionsParams={siteFieldOptionsParams}
            value={form.values[FIELD_SITE] ?? null}
            onChange={handleSiteChange}
            {...form.getMuiFieldErrorProps(FIELD_SITE)}
            data-testid="site_field"
          />
        </Grid>
        <Grid item={true} md={6} xs={12}>
          <InputAutocompleteQuery
            disabled={submitting}
            queryKeyPrefix="task_company"
            label="Company"
            placeholder="Search Companies…"
            options={companyFieldOptions}
            optionsParams={companyFieldOptionsParams}
            value={form.values[FIELD_COMPANY] ?? null}
            onChange={handleCompanyChange}
            {...form.getMuiFieldErrorProps(FIELD_COMPANY)}
            data-testid="company_field"
          />
        </Grid>
        <Grid item={true} md={6} xs={12}>
          <InputAutocompleteQuery
            disabled={submitting}
            queryKeyPrefix="task_system"
            label="System"
            placeholder="Search Systems…"
            options={systemFieldOptions}
            optionsParams={systemFieldOptionsParams}
            value={form.values[FIELD_SYSTEM] ?? null}
            onChange={form.getFieldValueChangeHandler(FIELD_SYSTEM)}
            {...form.getMuiFieldErrorProps(FIELD_SYSTEM)}
            data-testid="system_field"
          />
        </Grid>
        <Grid item={true} md={12} xs={12}>
          <TextField
            disabled={submitting}
            fullWidth={true}
            required={true}
            label="Title"
            variant="outlined"
            {...form.getFieldProps(FIELD_TITLE)}
            {...form.getMuiFieldErrorProps(FIELD_TITLE)}
            inputProps={{
              'data-testid': 'title_field',
            }}
          />
        </Grid>
        <Grid item={true} md={12} xs={12}>
          <TextField
            disabled={submitting}
            fullWidth={true}
            multiline={true}
            required={true}
            rows={4}
            label="Description"
            variant="outlined"
            {...form.getFieldProps(FIELD_DESCRIPTION)}
            {...form.getMuiFieldErrorProps(FIELD_DESCRIPTION)}
            inputProps={{
              'data-testid': 'description_field',
            }}
          />
        </Grid>
        <Grid item={true} md={6} xs={12}>
          <InputAutocompleteQuery
            disabled={submitting}
            queryKeyPrefix="task_status"
            label="Status"
            required={true}
            placeholder="Search Statuses…"
            options={statusFieldOptions}
            value={form.values[FIELD_STATUS] ?? null}
            onChange={form.getFieldValueChangeHandler(FIELD_STATUS)}
            {...form.getMuiFieldErrorProps(FIELD_STATUS)}
            data-testid="status_field"
          />
        </Grid>
        <Grid item={true} md={6} xs={12}>
          <InputAutocompleteQuery
            disabled={submitting}
            queryKeyPrefix="task_priority"
            label="Priority"
            required={true}
            placeholder="Search Priorities…"
            options={priorityFieldOptions}
            value={form.values[FIELD_PRIORITY] ?? null}
            onChange={form.getFieldValueChangeHandler(FIELD_PRIORITY)}
            {...form.getMuiFieldErrorProps(FIELD_PRIORITY)}
            data-testid="priority_field"
          />
        </Grid>
        <Grid
          item={true}
          md={12}
          xs={12}
          display="flex"
          gap={2}
          justifyContent="space-between"
        >
          <Button
            disabled={submitting}
            type="button"
            variant="outlined"
            color={COLOR_PRIMARY}
            onClick={handleCancelClick}
            data-testid="cancel_button"
          >
            {cancelLabel}
          </Button>
          <Button
            disabled={submitDisabled}
            type="submit"
            variant="contained"
            color={COLOR_PRIMARY}
            data-testid="submit_button"
          >
            {submitting ? submitLoadingLabel : submitLabel}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

TaskForm.propTypes = {
  submitting: PropTypes.bool,
  cancelLabel: PropTypes.string,
  submitLabel: PropTypes.string,
  submitLoadingLabel: PropTypes.string,
  errorMessage: PropTypes.string,
  values: PropTypes.shape({
    [FIELD_CONTACT]: PropTypes.number,
    [FIELD_SITE]: PropTypes.number,
    [FIELD_COMPANY]: PropTypes.number,
    [FIELD_SYSTEM]: PropTypes.number,
    [FIELD_TITLE]: PropTypes.string,
    [FIELD_DESCRIPTION]: PropTypes.string,
    [FIELD_STATUS]: PropTypes.oneOf([
      STATUS_CLOSED,
      STATUS_ON_HOLD,
      STATUS_OPEN,
      STATUS_RESOLVED,
    ]),
    [FIELD_PRIORITY]: PropTypes.oneOf([
      PRIORITY_LOW,
      PRIORITY_MEDIUM,
      PRIORITY_HIGH,
    ]),
  }),
  errors: PropTypes.shape({
    [FIELD_CONTACT]: PropTypes.string,
    [FIELD_SITE]: PropTypes.string,
    [FIELD_COMPANY]: PropTypes.string,
    [FIELD_SYSTEM]: PropTypes.string,
    [FIELD_TITLE]: PropTypes.string,
  }),
  contactFieldOptions: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.number.isRequired,
      }),
    ),
  ]),
  siteFieldOptions: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.number.isRequired,
      }),
    ),
  ]),
  companyFieldOptions: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.number.isRequired,
      }),
    ),
  ]),
  systemFieldOptions: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.number.isRequired,
      }),
    ),
  ]),
  statusFieldOptions: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.number.isRequired,
      }),
    ),
  ]),
  priorityFieldOptions: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.number.isRequired,
      }),
    ),
  ]),
  onChange: PropTypes.func,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

/**
 * @type {ServiceCallForm}
 */
export default memo(TaskForm);
